import { Injectable, computed, signal } from '@angular/core';
import { buildReportFilterParams } from '@pages/reports/report-single/filters/report-filters-transformer';
import type { IFilterValues } from '@pages/reports/types/filter-values.interface';
import type { IFilterItem, IReportConfiguration } from '@pages/reports/types/report-config.interface';

@Injectable({ providedIn: 'root' })
export class ReportFiltersStateService {
    readonly effectiveFilters = computed<IFilterValues>(() => ({
        ...this.mainFilters(),
        ...this.additionalFilters()
    }));

    private readonly mainFilters = signal<Partial<IFilterValues>>({});
    private readonly additionalFilters = signal<Partial<IFilterValues>>({});

    setMainFilters(filters: Partial<IFilterValues>): void {
        this.mainFilters.set(structuredClone(filters));
    }

    setAdditionalFilters(filters: Partial<IFilterValues>): void {
        this.additionalFilters.set(structuredClone(filters));
    }

    setAllFilters(filters: IFilterValues, config?: IReportConfiguration<unknown>): void {
        if (!config) {
            this.mainFilters.set(structuredClone(filters));
            this.additionalFilters.set({});
            return;
        }

        const mainKeys = this.extractRootKeys(config.filters.main ?? []);
        const additionalKeys = this.extractRootKeys(config.filters.additional ?? []);

        const main: Partial<IFilterValues> = {};
        const additional: Partial<IFilterValues> = {};

        for (const [key, value] of Object.entries(filters as Record<string, unknown>)) {
            if (mainKeys.has(key)) {
                (main as Record<string, unknown>)[key] = structuredClone(value);
            }
            if (additionalKeys.has(key)) {
                (additional as Record<string, unknown>)[key] = structuredClone(value);
            }
        }

        this.mainFilters.set(main);
        this.additionalFilters.set(additional);
    }

    toApiPayload(config?: IReportConfiguration<unknown>): Record<string, unknown> {
        if (!config) return {};
        return buildReportFilterParams(this.effectiveFilters(), config);
    }

    private extractRootKeys(items: IFilterItem[]): Set<string> {
        const keys = new Set<string>();
        for (const item of items) {
            if (item.fieldName) keys.add(item.fieldName);
            if (item.mapping) {
                Object.values(item.mapping).forEach((path: unknown) => {
                    keys.add(String(path).split('.')[0]);
                });
            }
        }
        return keys;
    }
}
